import classnames from 'classnames';
import { debounce } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useDispatch, useSelector } from 'react-redux';

import { FilterIcon } from '../../assets/icons';
import Post from '../../components/forum/Post';
import PostForm from '../../components/forum/PostForm';
import { Select } from '../../components/_shared';
import { isEnterprise } from '../../config';
import { AppContext } from '../../providers/AppProvider';
import { deletePost } from '../../service/forum';
import { addPost, fetchForum } from '../../store/actions/course';
import { makeSelectForum, makeSelectIsIcscOrg } from '../../store/selectors';
import { notify } from '../../utils/helper';
import { Container, MenuContainer, Searchbar } from './Forum.styled';

const SORT_OPTIONS = ['Title', 'Author', 'Posted Date'];

const Forum = ({ forumId }) => {
  const dispatch = useDispatch();
  const postsArr = useSelector(makeSelectForum());
  const [key, setKey] = useState(1);
  const [showOptions, setShowOptions] = useState(false);
  const [sortBy, setSortBy] = useState('Posted Date');
  const isIcsc = useSelector(makeSelectIsIcscOrg());

  const {query, setQuery} = useContext(AppContext);

  useEffect(() => {
    dispatch(fetchForum({ forumId, query }));
  }, [forumId, query]);

  useEffect(() => {
    setSortBy('Posted Date');
    setQuery('');
  }, [forumId]);

   const handleToggleShowOptions = () => setShowOptions((v) => !v);

  const handleSortOptionChange = (v) => {
    setSortBy(v.key);
  };

  const handleNewPost = (data, cb) => {
    dispatch(
      addPost({
        ...data,
        parentId: forumId,
        link: window.location.href,
        type: isIcsc ? 'icsc' : isEnterprise ? 'enterprise' : '',
        cb: () => {
          dispatch(fetchForum({ forumId, query }));

          setKey(key + 1);
          if (typeof cb === 'function') {
            cb();
          }
        },
      })
    );
  };
  
  const debouncedSearch = debounce((v) => setQuery(v), 250);

  const handleChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleAddComment = (pId) => (data) => {
    dispatch(
      addPost({
        ...data,
        parentId: pId,
        isComment: true,
        link: window.location.href,
        type: isIcsc ? 'icsc' : isEnterprise ? 'enterprise' : '',
        cb: () => {
          dispatch(fetchForum({ forumId, query }));
        },
      })
    );
  };

  const handleDeletePost = (pId) => async () => {
    if (window.confirm('Do you really want to delete this post/comment?')) {
      try {
        await deletePost(pId, {
          link: window.location.href,
          type: isIcsc ? 'icsc' : isEnterprise ? 'enterprise' : '',
        });
        dispatch(fetchForum({ forumId, query }));
        notify('success', 'Deleted post/comment successfully!');
      } catch (e) {
        notify('error', `Failed to delete: ${e && e.toString()}`);
      }
    }
  };

  const posts = useMemo(() => {
    const cPosts = postsArr.slice();

    if (sortBy === 'Title') {
      cPosts.sort((a, b) => (a.title > b.title ? -1 : 1));
    } else if (sortBy === 'Author') {
      cPosts.sort((a, b) =>
        a?.author?.name > b?.author?.name ||
        a?.author?.username > b?.author?.username
          ? -1
          : 1
      );
    } else if (sortBy === 'Posted Date') {
      cPosts.sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : 1));
    }

    return cPosts;
  }, [postsArr, sortBy]);

  return (
    <Container>
      {query === '' && (
        <div className="title">All Questions About This Course</div>
      )}
      <div className="w-full flex items-center justify-between mb-3 relative">
        <Searchbar onChange={handleChange} placeholder="Search Topics"/>
        <div
          className={classnames(
            'filter-btn text-white ml-9 cursor-pointer hover:underline whitespace-no-wrap bg-light-yellow',
            showOptions && 'active'
          )}
          onClick={handleToggleShowOptions}
        >
          <FilterIcon />
        </div>
        {showOptions && (
          <OutsideClickHandler onOutsideClick={() => setShowOptions(false)}>
            <MenuContainer>
              <div className="label">Sort by</div>
              <Select
                className="border border-solid border-light-gray rounded-lg"
                value={{
                  key: sortBy,
                  label: sortBy,
                }}
                options={SORT_OPTIONS.map((option) => ({
                  key: option,
                  label: option,
                }))}
                placeholder="Ex: Author"
                onChange={handleSortOptionChange}
              />
            </MenuContainer>
          </OutsideClickHandler>
        )}
      </div>
      {posts.length === 0 && (
        <div className="text-center text-base my-4">No posts.</div>
      )}
            
      {posts
        .map((post) => (
          <Post
            key={post.id}
            {...post}
            onAddComment={handleAddComment(post.id)}
            onDelete={handleDeletePost}
          />
        ))}
      <div className="mt-12 shadow-lg">
        <PostForm key={key} onSubmit={handleNewPost} />
      </div>
    </Container>
  );
};

export default Forum;
